import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { ConnectionLimitation, ConnectionsDetail, ConnectionView, DiagnoseStep } from '../../apis';

type Tone = 'good' | 'warn' | 'muted' | 'bad';

/** How a step's status reads at a glance: settled, open, not run, broken. */
const TONE: Record<string, Tone> = {
  significant: 'good', ruled_out: 'good', found: 'good', matched: 'good',
  plausible: 'warn', data_late: 'warn', immature: 'warn', partly_open: 'warn', unverified: 'warn',
  within_normal: 'muted', insufficient_data: 'muted', skipped: 'muted', none: 'muted', pending: 'muted',
  error: 'bad',
};

const COLOR: Record<Tone, string> = { good: 'var(--ok)', warn: 'var(--warn)', muted: 'var(--ink-3)', bad: 'var(--bad)' };

export function stepStatusLabel(status: string): string {
  return String(status || '').replace(/_/g, ' ');
}

/**
 * How a limitation reads: the words the server rendered for THIS viewer, or — when the detail was not
 * theirs to read — the generic note the server put in its place. The browser never composes either.
 */
function limitationText(l: ConnectionLimitation): string {
  return l.text || String(l.code || '').replace(/_/g, ' ');
}

function Limitations({ owner, lims }: { owner: string; lims: ConnectionLimitation[] | undefined }) {
  return (
    <>
      {(lims || []).map((l, i) => (
        <Typography key={`${owner}-${l.code}-${i}`} data-testid="walk-limitation" data-code={l.code}
          sx={{ gridColumn: 2, fontSize: 12, color: 'text.secondary', pl: 1.5 }}>
          {'⚠ '}{limitationText(l)}
        </Typography>
      ))}
    </>
  );
}

/**
 * One connection, with its qualifications under it. A qualification is INSEPARABLE from the result it
 * qualifies (#419), so the server sends the COMPLETE set with each result — its own, the ones on the
 * comparisons it rests on, and the step's — and this renders all of them. Dropping any would make the
 * result read stronger than the walk was. A result the server downgraded says so.
 */
function ConnectionRow({ c }: { c: ConnectionView }) {
  return (
    <Box data-testid="walk-connection" data-connection={c.id} data-relation={c.relation}
      data-tier={c.tier || 'unconfirmed'} data-downgraded={c.downgraded ? 'true' : 'false'}
      sx={{ gridColumn: 2, display: 'grid', gridTemplateColumns: 'subgrid' }}>
      <Typography sx={{ gridColumn: 2, fontSize: 12, color: 'text.secondary' }}>
        <Box component="span" sx={{ fontWeight: 600 }}>{String(c.relation || '').replace(/_/g, ' ')}</Box>
        {c.headline ? ` — ${c.headline}` : ''}
        {` · ${c.tier || 'unconfirmed'}`}
        {c.downgraded ? ' · downgraded' : ''}
      </Typography>
      <Limitations owner={c.id} lims={c.limitations} />
    </Box>
  );
}

/**
 * The fifth step's detail (#426), rendered only when the envelope carries it. Every connection with its
 * complete qualification set, then every comparison with its own — including the ones a connection rests
 * on, so a reader can see what each comparison was qualified by. The step's own limitations are shown when
 * nothing was admitted; when results were, each already carries them.
 */
function ConnectionsDetailRows({ detail }: { detail: ConnectionsDetail }) {
  const connections = detail.connections || [];
  const comparisons = detail.comparisons || [];
  return (
    <>
      {connections.map((c) => <ConnectionRow key={c.id} c={c} />)}
      {comparisons.map((m) => (
        <Box key={m.id} data-testid="walk-comparison" data-comparison={m.id} data-tier={m.tier || 'unconfirmed'}
          sx={{ gridColumn: 2, display: 'grid', gridTemplateColumns: 'subgrid' }}>
          <Typography sx={{ gridColumn: 2, fontSize: 12, color: 'text.secondary' }}>
            {m.headline || m.id}{` · ${m.tier || 'unconfirmed'}`}{m.downgraded ? ' · downgraded' : ''}
          </Typography>
          <Limitations owner={m.id} lims={m.limitations} />
        </Box>
      ))}
      {connections.length === 0 && comparisons.length === 0
        ? <Limitations owner="step" lims={detail.limitations} />
        : null}
    </>
  );
}

/**
 * The walk's steps (#184), in the order they always run: significance, boring checks, attribution,
 * matched events. A step that did not run is shown as such, with its reason — the boring answers are
 * never skipped silently. An envelope produced under the v2 contract (#426) carries a fifth step,
 * `connections`; this list renders whatever steps it is given, so a four-step envelope looks exactly
 * as it always has.
 */
export default function WalkSteps({ steps }: { steps: DiagnoseStep[] | null | undefined }) {
  if (!steps || steps.length === 0) return null;
  return (
    <Box component="ol" data-testid="walk-steps" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 0, border: '1px solid var(--line-soft)', borderRadius: '10px', overflow: 'hidden' }}>
      {steps.map((s) => {
        const tone = TONE[s.status] || 'muted';
        return (
          <Box
            component="li"
            key={s.id}
            data-testid="walk-step"
            data-step={s.id}
            data-status={s.status}
            sx={{ display: 'grid', gridTemplateColumns: '10px 1fr', columnGap: 1, alignItems: 'baseline', px: 1.5, py: 1, bgcolor: 'var(--surface2)', '&:not(:last-child)': { borderBottom: '1px solid var(--line-soft)' } }}
          >
            <Box aria-hidden sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: COLOR[tone], mt: '5px' }} />
            <Typography sx={{ fontSize: 12.5, lineHeight: 1.5, color: tone === 'muted' ? 'text.secondary' : 'text.primary' }}>
              <Box component="span" sx={{ fontWeight: 600 }}>{s.label}</Box>
              <Box component="span" sx={{ color: COLOR[tone], fontWeight: 600 }}>{' · '}{stepStatusLabel(s.status)}</Box>
              {s.summary ? <Box component="span" sx={{ color: 'text.secondary' }}>{' — '}{s.summary}</Box> : null}
            </Typography>
            {s.id === 'boring_checks' && (s.detail?.checks || []).map((check: any) => (
              <Typography key={check.check} data-testid="walk-check" data-check={check.check}
                sx={{ gridColumn: 2, fontSize: 12, color: 'text.secondary' }}>
                {check.check} · {stepStatusLabel(check.status)} — {check.reason}
              </Typography>
            ))}
            {s.id === 'connections' && s.detail
              ? <ConnectionsDetailRows detail={s.detail as ConnectionsDetail} />
              : null}
          </Box>
        );
      })}
    </Box>
  );
}
